import {find, forEach, groupBy} from "lodash";
import moment from "moment";
import {IColumnMetadata} from "@/features/application/api/applicationModels";
import {
    ComparisonMethodType,
    IMonitorConfiguration,
    MonitorCriteriaTypeEnum,
    TransactionTenderType
} from "@/features/monitors/api/monitorsModels";
import {DataType} from "./dataFormatUtil";
import {IMonitorDetailsViewModel} from "@/features/monitors/api/monitorDetails/monitordetailsModels";
import {IChartData, IDataSeries} from "@/features/metricDetails/api/chartModels";
import {DayPartType} from "@/features/metricDetails/api/metricDayPartDataModels";
import {
    DrilldownType,
    MetricCode
} from "@/features/metricDetails/api/metricDetailsModels";
import store from "@/state/store";
import {
    ModuleType,
    PageCode
} from "@/features/configuration/api/uiConfigurationModels";

export const orderChannelAllKey = "ALL";

const monitorDataUtil = {
    // This function return a IColumnMetaData from a metric key
    // We should be able to remove this function if we update IMonitorConfiguration and add IMetricDefinition or IColumnMetaData
    getColumnDefinitionFromMetricKey(metricKey: MetricCode): IColumnMetadata {
        // NOTE: We could pass the needed metric definitions into this functions,
        // but since they are already in redux, then we can get them from there. Make sure
        // that every dropdown metric has an instance in ClientConceptMetricDefinition
        const clientConcept = store.getState().application.selectedClientConcept;

        const selectedMetric = find(
            store.getState().application.clientConceptMetricDefinitions,
            (m) => {
                return (
                    m.metricCode.toLowerCase() === metricKey.toLowerCase() &&
                    m.clientConcept_PK === clientConcept.clientConcept_PK
                );
            }
        );

        const columnDefinition: IColumnMetadata = selectedMetric
            ? {
                  key: selectedMetric.metricCode,
                  label: selectedMetric.alias,
                  dataType: selectedMetric.dataType,
                  decimalPlaces: selectedMetric.decimalPlaces
              }
            : {
                  key: metricKey,
                  label: metricKey,
                  dataType: DataType.Number,
                  decimalPlaces: 0
              };

        return columnDefinition;
    },

    getDefaultMonitorConfiguration(
        formAction: "NONE" | "SEARCH" | "SAVE" | "EDIT" | "DELETE" | "VIEW-ONLY"
    ): IMonitorConfiguration {
        const defaultMonitorConfiguration: IMonitorConfiguration = {
            formAction: formAction,
            type: MonitorCriteriaTypeEnum.Transaction,
            metricCriteria: [
                {
                    metric: MetricCode.Unknown,
                    measure: ComparisonMethodType.Equal,
                    amount: null
                }
            ],
            tenderType: [TransactionTenderType.All],
            hourOfDayStart: 6,
            hourOfDayEnd: 29,
            orderChannel: [orderChannelAllKey],
            isTrendSearch: false
        };
        if (formAction === "SEARCH") {
            defaultMonitorConfiguration.searchStartDate = moment()
                .startOf("day")
                .add(-29, "days")
                .toDate();
            defaultMonitorConfiguration.searchEndDate = moment()
                .startOf("day")
                .add(-1, "days")
                .toDate();
        }
        return defaultMonitorConfiguration;
    },

    createMonitorTrendChartData(
        monitorTrendViewModel: IMonitorDetailsViewModel,
        searchType: MonitorCriteriaTypeEnum
    ): IChartData {
        const monitorTrendResults =
            searchType === MonitorCriteriaTypeEnum.Transaction
                ? monitorTrendViewModel.transactionDetailData
                : monitorTrendViewModel.locationDetailData;

        // Group results by date and count the number of occurrences
        const resultsDataGroupedByDate: {[formattedDate: string]: number} = {};
        forEach(groupBy(monitorTrendResults, "busDt"), (value, key) => {
            resultsDataGroupedByDate[moment(key).format("MMM DD YYYY")] =
                value.length;
        });

        // Prepare data series object
        const monitorTrendChartResults: IDataSeries[] = [
            {
                id: "monitorTrendDataOccurrences",
                name: "Detector Occurrences",
                data: [],
                visible: true
            }
        ];

        let tempDate = monitorTrendViewModel.searchStartDate;
        // Loop through all days between searchStartDate and searchEndDate and assign number of occurrences to each date
        while (moment(tempDate) <= moment(monitorTrendViewModel.searchEndDate)) {
            const date = moment(tempDate);
            const formattedDate = date.format("MMM DD YYYY");

            // Assign the number of occurrences to current date
            if (resultsDataGroupedByDate[formattedDate] !== undefined) {
                monitorTrendChartResults[0].data.push([
                    date.valueOf(), // x value
                    resultsDataGroupedByDate[formattedDate]
                ]);
            } else {
                monitorTrendChartResults[0].data.push([date.valueOf(), 0]); // y value
            }

            // Add day
            tempDate = date.add(1, "days").toDate();
        }

        // This is the data object expected by the TrendColumnChart component
        const data: IChartData = {
            id: "monitorTrendChart",
            title: "Occurrences Over Time",
            subTitle: "Click and drag in the plot area to zoom in",
            yAxis: [
                {
                    title: "Occurrences"
                }
            ],
            series: monitorTrendChartResults,
            // MetricDefinition is being passed in for data formatting.
            // We create a dummy metric definition to just display integers
            metricDefinitions: [
                {
                    id: 0,
                    page_PK: 0,
                    pageCode: PageCode.Unknown,
                    clientConcept_PK: 0,
                    moduleType: ModuleType.Unknown,
                    metricCode: "",
                    metricName: "",
                    dataType: DataType.Number,
                    sortOrder: 0,
                    toggleOrder: 0,
                    metricOrder: 0,
                    decimalPlaces: 0,
                    dayPartType: DayPartType.Unknown,
                    drilldownType: DrilldownType.None
                }
            ]
        };

        return data;
    }
};

export default monitorDataUtil;
